import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, OTOFLEX } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 *
 * @author  @omerrkosar
 */

const defaultColSpan = {
	md: 6
};

const AddTransactionForm = NiceModal.create(({ id, modalName, mode, fullName, balance }) => {
	const viewMode = useMemo(() => mode === 'view', [mode]);
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					type: yup.string().nullable().required(),
					amount: yup.number().nullable().required(),
					date: yup.string().nullable(),
					currency: yup.string().nullable()
				})
				.required(),
		[]
	);

	const [isLoading, setIsLoading] = useState(false);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { closeModal } = useModalRouter();

	const { Currency } = useLocale();
	const { showSuccess, showError } = useAlert();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			type: null,
			amount: null,
			date: new Date().toISOString(),
			currency: Currency
		}
	});

	const {
		watch,
		setValue,
		handleSubmit,

		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = (data) => {
		setIsLoading(true);

		axiosPrivate
			.post(OTOFLEX.ADD_TRANSACTION, {
				userID: id,
				type: data.type,
				amount: data.amount,
				date: data.date,
				currency: Currency
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.OTO_FLEX_DRIVERS));
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => setIsLoading(false));
	};

	return (
		<Modal fullwidth maxWidth="medium" name={modalName} className="modal-driver">
			<ModalTitle>
				<Typography variant="p1">addTransaction</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="driverName" isLoading={isLoading} required>
						<StringInput disabled={viewMode} value={fullName ?? null} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="balance" isLoading={isLoading} required>
						<IntegerInput disabled={viewMode} unit={Currency} value={balance ?? null} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="type" isLoading={isLoading} required>
						<EnumSelect
							name="type"
							enumName={ENUMS.DRIVER_CASH_TRANSACTION_TYPE}
							setValue={(value) => {
								setValue(`type`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`type`)}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="amount" isLoading={isLoading} required>
						<IntegerInput name="amount" unit={Currency} allowFloat />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="date" isLoading={isLoading}>
						<DateInput
							name="date"
							value={watch('date')}
							setValue={(value) => {
								setValue('date', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />

				<Button
					loading={isLoading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="button.add"
				/>
			</ModalActions>
		</Modal>
	);
});

export default AddTransactionForm;
